import { useEffect, useState } from "react";
import { Smartphone, X, Share } from "lucide-react";
import { safeStorage } from "@/lib/storage";

/**
 * "Install DS-260USA" banner. Detects both:
 *   • Chrome/Android/desktop → uses `beforeinstallprompt` event.
 *   • iOS Safari → shows manual "Add to Home Screen" instructions
 *     (iOS never fires beforeinstallprompt).
 * Dismissal is remembered in localStorage for 30 days.
 */
const DISMISS_KEY = "pwa-install-dismissed-at";
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function wasRecentlyDismissed() {
  const ts = Number(safeStorage.get(DISMISS_KEY) || 0);
  return ts && Date.now() - ts < THIRTY_DAYS_MS;
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
  // Modern iPads report as MacIntel with touch — detect that too.
  const isTouchMac =
    /Macintosh/.test(ua) && "ontouchend" in document;
  return isIOSDevice || isTouchMac;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return undefined;

    // Android / Chrome path
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS path — no native prompt, show hint after a short delay so it doesn't
    // fire on the very first paint.
    let iosTimer = null;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    safeStorage.set(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosHint(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setVisible(false);
      setDeferred(null);
    } else if (isIos()) {
      setShowIosHint(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed left-1/2 -translate-x-1/2 z-40 w-[calc(100%-24px)] max-w-md"
        style={{ bottom: "max(16px, env(safe-area-inset-bottom))" }}
        data-testid="install-prompt-banner"
      >
        <div
          className="card-surface p-4 flex items-center gap-3 shadow-2xl anim-in"
          style={{ boxShadow: "0 20px 50px -15px rgba(26,46,36,0.35)" }}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "var(--action)" }}
          >
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
              Install DS-260USA
            </div>
            <div className="text-[11px] leading-snug" style={{ color: "var(--text-secondary)" }}>
              Snap document photos, get case updates — feels like an app.
            </div>
          </div>
          <button
            data-testid="install-prompt-install-btn"
            onClick={install}
            className="btn-primary text-xs px-3 py-2 shrink-0"
          >
            Install
          </button>
          <button
            data-testid="install-prompt-dismiss-btn"
            onClick={dismiss}
            className="p-1.5 rounded hover:bg-black/5 shrink-0"
            aria-label="Dismiss install prompt"
          >
            <X className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
      </div>

      {showIosHint && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(26,46,36,0.55)" }}
          onClick={dismiss}
          data-testid="ios-install-hint"
        >
          <div
            className="card-surface p-6 w-full max-w-sm anim-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Share className="w-5 h-5" style={{ color: "var(--action)" }} />
              <div className="font-display font-bold text-lg">Add to Home Screen</div>
            </div>
            <ol
              className="text-sm space-y-2 list-decimal pl-5"
              style={{ color: "var(--text-primary)" }}
            >
              <li>
                Tap the <b>Share</b> button in Safari
                <span
                  className="inline-block ml-1 px-1.5 py-0.5 rounded"
                  style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
                >
                  <Share className="w-3 h-3 inline" />
                </span>
              </li>
              <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
              <li>Tap <b>Add</b> — DS-260USA opens like a real app.</li>
            </ol>
            <button
              onClick={dismiss}
              className="btn-primary w-full mt-5 text-sm"
              data-testid="ios-install-hint-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
